import { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot, limit, Timestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import type { Booking, Space } from '@/types'
import {
  Calendar,
  Clock,
  LayoutGrid,
  ArrowRight,
  TrendingUp,
  DollarSign,
  Plus,
  ShieldAlert,
  Star,
  CalendarPlus,
  Users
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { RESOURCE_TYPE_LABELS } from '@/utils/constants'
import { PLANS } from '@/utils/plans'
import { Plan } from '@/types'

// Reusable Components
// Reusable Components
import PageHeader from '@/components/layout/PageHeader'
import LoadingState from '@/components/ui/LoadingState'
import OccupancyChart from '@/components/analytics/OccupancyChart'
import RevenueChart from '@/components/analytics/RevenueChart'
import BookingStatusChart from '@/components/analytics/BookingStatusChart'

// Hooks
import { useAnalytics } from '@/hooks/useAnalytics'

export default function DashboardPage() {
  const { currentOrg, userData, userRole } = useAuthStore()
  const [stats, setStats] = useState({
    totalMonthlyBookings: 0,
    totalMonthlyRevenue: 0,
    pendingPayments: 0,
    pendingApprovals: 0
  })
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([])
  const [spaces, setSpaces] = useState<Space[]>([])
  const [recentReviews, setRecentReviews] = useState<any[]>([])
  const [isLoadingMain, setIsLoadingMain] = useState(true)

  const { analytics, loading: analyticsLoading } = useAnalytics(currentOrg?.id)

  const isAdmin = userRole === 'owner' || userRole === 'admin'

  useEffect(() => {
    if (!currentOrg) return

    setIsLoadingMain(true)

    // Listeners for Real-time Updates
    let unsubBookings = () => { }
    let unsubSpaces = () => { }
    let unsubReviews = () => { }

    try {
      // 1. Reservas Recentes (Real-time)
      let bookingsQuery = query(
        collection(db, 'bookings'),
        where('orgId', '==', currentOrg.id)
      )

      // Se não for admin, vê apenas as suas
      if (!isAdmin && userData?.id) {
        bookingsQuery = query(
          collection(db, 'bookings'),
          where('orgId', '==', currentOrg.id),
          where('userId', '==', userData.id)
        )
      }

      unsubBookings = onSnapshot(bookingsQuery, (snapshot) => {
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Booking[]

        // Sort in memory
        bookingsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0)
          const dateB = b.createdAt?.toDate?.() || new Date(0)
          return dateB.getTime() - dateA.getTime()
        })

        setUpcomingBookings(bookingsData.slice(0, 5))

        // Calculate Stats
        if (isAdmin) {
          const revenue = bookingsData
            .filter(b => b.status === 'CONFIRMED' || b.status === 'COMPLETED')
            .reduce((acc, curr) => acc + (curr.totalPrice || 0), 0)

          const pendingPay = bookingsData.filter(b => b.status === 'PENDING_PAYMENT').length
          const pendingApp = bookingsData.filter(b => b.status === 'PENDING_APPROVAL').length

          setStats({
            totalMonthlyBookings: bookingsData.length,
            totalMonthlyRevenue: revenue,
            pendingPayments: pendingPay,
            pendingApprovals: pendingApp
          })
        } else {
          setStats({
            totalMonthlyBookings: bookingsData.length,
            totalMonthlyRevenue: 0,
            pendingPayments: bookingsData.filter(b => b.status === 'PENDING_PAYMENT').length,
            pendingApprovals: bookingsData.filter(b => b.status === 'PENDING_APPROVAL').length
          })
        }
        setIsLoadingMain(false)
      }, (error) => {
        console.error("Error listening to bookings:", error)
        setIsLoadingMain(false)
      })

      // 2. Spaces (Real-time)
      const spacesQuery = query(
        collection(db, 'spaces'),
        where('orgId', '==', currentOrg.id),
        limit(3)
      )
      unsubSpaces = onSnapshot(spacesQuery, (snapshot) => {
        const spacesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Space[]
        setSpaces(spacesData)
      })

      // 3. Reviews (Admin only)
      if (isAdmin) {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          // where('orgId', '==', currentOrg.id), // Uncomment if reviews are filtered by org
          limit(5)
        )
        unsubReviews = onSnapshot(reviewsQuery, (snapshot) => {
          const reviews = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          setRecentReviews(reviews)
        }, (error) => {
          console.error("Error listening to reviews:", error)
        })
      }

    } catch (error) {
      console.error(error)
      setIsLoadingMain(false)
    }

    return () => {
      unsubBookings()
      unsubSpaces()
      unsubReviews()
    }
  }, [currentOrg, isAdmin, userData?.id])

  if (!currentOrg) return null

  if (isLoadingMain || analyticsLoading) {
    return <LoadingState />
  }

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-12 space-y-12 animate-in fade-in duration-500">
      <PageHeader
        title={`Olá, ${userData?.displayName?.split(' ')[0] || 'Usuário'}`}
        description={
          <div className="flex flex-col gap-2">
            <span className="text-[#001c28]/80 font-medium">Bem-vindo à sua central de comando. Gerencie {currentOrg.name} com precisão e estilo.</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs font-bold text-[#001c28] bg-[#001c28]/5 px-3 py-1.5 rounded-lg border border-[#001c28]/10 tracking-wide lowercase">
                {userData?.email}
              </span>
              {currentOrg.plan && (
                <span className="text-[10px] font-black text-[#d4a94d] uppercase tracking-widest bg-[#d4a94d]/10 px-3 py-1.5 rounded-lg border border-[#d4a94d]/20">
                  {currentOrg.plan}
                </span>
              )}
            </div>
          </div>
        }
      />

      {/* Admin Insights - Colorful & Premium */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mt-12">
        <div className="glass-card bg-gradient-to-br from-blue-600 to-blue-900 text-white !border-0 shadow-blue-900/30 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-20 transform rotate-12 group-hover:scale-125 transition-transform duration-700">
            <TrendingUp size={80} />
          </div>
          <div className="relative z-10">
            <p className="text-blue-100 font-black uppercase tracking-widest text-xs mb-2 opacity-80">{isAdmin ? 'Reservas no Mês' : 'Minhas Reservas'}</p>
            <h3 className="text-5xl font-black tracking-tighter mb-4">{stats.totalMonthlyBookings}</h3>
            <div className="inline-flex items-center bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
              <TrendingUp size={14} className="mr-2" />
              <span className="text-[10px] font-bold uppercase tracking-wider">+12% este mês</span>
            </div>
          </div>
        </div>

        {isAdmin ? (
          <div className="glass-card bg-gradient-to-br from-[#001c28] to-[#00334e] text-white !border-0 shadow-[#001c28]/30 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-10 transform -rotate-12 group-hover:scale-125 transition-transform duration-700">
              <DollarSign size={80} />
            </div>
            <div className="relative z-10">
              <p className="text-slate-400 font-black uppercase tracking-widest text-xs mb-2">Receita Mensal</p>
              <h3 className="text-4xl font-black tracking-tighter mb-4 text-[#d4a94d]">R$ {stats.totalMonthlyRevenue.toFixed(2)}</h3>
              <div className="inline-flex items-center bg-[#d4a94d]/20 text-[#d4a94d] px-3 py-1 rounded-full backdrop-blur-sm border border-[#d4a94d]/20">
                <DollarSign size={14} className="mr-2" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Faturamento</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="glass-card bg-gradient-to-br from-purple-600 to-purple-900 text-white !border-0 shadow-purple-900/30 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-20 transform rotate-45 group-hover:scale-125 transition-transform duration-700">
              <Star size={80} />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-purple-200 font-black uppercase tracking-widest text-xs">Seu Plano</p>
              </div>
              <h3 className="text-3xl font-black tracking-tight mb-6">{PLANS[currentOrg.plan as Plan]?.name || currentOrg.plan}</h3>
              <Link to="/upgrade-plan" className="inline-block bg-white text-purple-700 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-50 transition-colors shadow-lg">
                {currentOrg.plan === 'ultra' ? 'Gerenciar' : 'Fazer Upgrade'}
              </Link>
            </div>
          </div>
        )}

        <Link to={isAdmin ? "/admin/payments" : "/bookings"} className="block group">
          <div className="glass-card bg-gradient-to-br from-amber-500 to-orange-600 text-white !border-0 shadow-orange-500/30 relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 p-8 opacity-20 transform rotate-12 group-hover:translate-x-2 transition-transform duration-500">
              <Clock size={80} />
            </div>
            <div className="relative z-10">
              <p className="text-amber-100 font-black uppercase tracking-widest text-xs mb-2">Pendente Pix</p>
              <h3 className="text-5xl font-black tracking-tighter mb-2">{stats.pendingPayments}</h3>
              <p className="text-[10px] font-bold uppercase tracking-wide opacity-80">Aguardando confirmação</p>
            </div>
          </div>
        </Link>

        <Link to={isAdmin ? "/approvals" : "/bookings/search"} className="block group">
          <div className="glass-card bg-gradient-to-br from-emerald-500 to-teal-700 text-white !border-0 shadow-emerald-500/30 relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 p-8 opacity-20 transform -rotate-12 group-hover:translate-x-2 transition-transform duration-500">
              {isAdmin ? <ShieldAlert size={80} /> : <Plus size={80} />}
            </div>
            <div className="relative z-10">
              <p className="text-emerald-100 font-black uppercase tracking-widest text-xs mb-2">{isAdmin ? 'Aprovações' : 'Nova Reserva'}</p>
              <h3 className="text-5xl font-black tracking-tighter mb-2">{isAdmin ? stats.pendingApprovals : '+'}</h3>
              <p className="text-[10px] font-bold uppercase tracking-wide opacity-80">{isAdmin ? 'Requer atenção' : 'Agendar espaço'}</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Analytics Charts (Admin Only) */}
      {isAdmin && analytics && (stats.totalMonthlyBookings > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 my-20">
          <RevenueChart data={analytics.revenueByMonth} />
          <BookingStatusChart data={analytics.bookingsByStatus} />
          <div className="lg:col-span-2">
            <OccupancyChart data={analytics.popularSpaces.map(s => ({ name: s.spaceName, reservas: s.bookingCount || 0, receita: (s as any).totalRevenue || 0 }))} />
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mt-16">
        {/* Left Column: Activity */}
        <div className="lg:col-span-3 space-y-10">
          <div className="flex items-center justify-between border-b-2 border-[#001c28]/5 pb-6">
            <h2 className="text-xl font-black text-[#001c28] uppercase tracking-tight flex items-center">
              <Clock className="w-6 h-6 mr-4 text-[#d4a94d]" /> Atividade Recente
            </h2>
            <Link to="/bookings" className="text-[10px] font-black text-[#001c28] uppercase tracking-widest hover:text-[#d4a94d] flex items-center transition-colors group">
              Ver Tudo <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {upcomingBookings.length === 0 ? (
            <div className="bg-white rounded-[3rem] p-20 text-center border-4 border-dashed border-[#001c28]/5 shadow-2xl shadow-[#001c28]/5">
              <div className="w-20 h-20 bg-[#001c28]/5 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-10 h-10 text-[#001c28]/20" />
              </div>
              <p className="text-[#001c28]/40 font-bold uppercase tracking-wide text-sm italic">Nenhuma atividade registrada ainda.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => {
                const startDate = booking.start instanceof Timestamp
                  ? booking.start.toDate()
                  : (booking.start as any)?.toDate?.() || new Date()

                return (
                  <Link
                    key={booking.id}
                    to={`/bookings/${booking.id}`}
                    className="bg-white p-8 rounded-[2.5rem] border-2 border-transparent hover:border-[#d4a94d]/20 hover:shadow-2xl hover:shadow-[#001c28]/10 transition-all duration-300 flex items-center justify-between group shadow-2xl shadow-[#001c28]/5"
                  >
                    <div className="flex items-center space-x-6">
                      <div className="w-14 h-14 rounded-2xl bg-[#001c28]/5 flex items-center justify-center text-[#001c28]/30 group-hover:bg-[#001c28] group-hover:text-[#d4a94d] transition-all">
                        <Calendar className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-black text-[#001c28] uppercase text-xs tracking-tight">{(booking as any).spaceName || 'Reserva #' + booking.id.slice(0, 4)}</p>
                        <p className="text-[10px] text-[#001c28]/30 font-black uppercase mt-1.5 tracking-widest italic">
                          {startDate.toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <span className={`px-5 py-2.5 rounded-2xl text-[9px] font-black uppercase tracking-[0.2em] border-2 ${booking.status === 'CONFIRMED' ? 'bg-green-50 text-green-600 border-green-100' :
                      booking.status === 'PENDING_APPROVAL' ? 'bg-orange-50 text-orange-600 border-orange-100' :
                        booking.status === 'CANCELED' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-[#001c28]/5 text-[#001c28] border-[#001c28]/10'
                      }`}>
                      {booking.status === 'CONFIRMED' ? 'Confirmado' :
                        booking.status === 'PENDING_APPROVAL' ? 'Pendente' :
                          booking.status === 'PENDING_PAYMENT' ? 'Aguardando Pix' : booking.status}
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        {/* Right Column: Mini Catalog */}
        <div className="lg:col-span-2 space-y-10">
          <div className="flex items-center justify-between border-b-2 border-[#001c28]/5 pb-6">
            <h2 className="text-xl font-black text-[#001c28] uppercase tracking-tight flex items-center">
              <LayoutGrid className="w-6 h-6 mr-4 text-[#d4a94d]" /> Seus Espaços
            </h2>
            <Link to="/bookings/catalog" className="bg-[#d4a94d] text-white px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#001c28] transition-all shadow-lg shadow-[#d4a94d]/20">Reservar</Link>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {spaces.length === 0 ? (
              <div className="bg-white rounded-[3rem] p-12 text-center border-4 border-dashed border-[#001c28]/5 shadow-2xl shadow-[#001c28]/5">
                <p className="text-[#001c28]/40 font-bold uppercase tracking-wide text-sm italic">Nenhum espaço configurado.</p>
              </div>
            ) : (
              spaces.map(space => (
                <div key={space.id} className="bg-white p-6 rounded-[2.5rem] flex items-center space-x-6 group hover:shadow-2xl hover:shadow-[#001c28]/10 transition-all border border-[#001c28]/5 hover:border-[#d4a94d]/20 shadow-xl shadow-[#001c28]/5">
                  <div className="w-24 h-24 bg-[#001c28]/5 rounded-[1.8rem] overflow-hidden shrink-0 ring-4 ring-[#001c28]/5 shadow-inner relative">
                    {space.photos?.[0] ? (
                      <img src={space.photos[0]} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#001c28]/10">
                        <LayoutGrid className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-[#001c28] text-sm tracking-tight truncate group-hover:text-[#d4a94d] transition-colors">{space.name}</p>
                    <span className="inline-block mt-2 px-3 py-1 bg-[#001c28]/5 text-[#001c28]/50 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[#001c28]/10">
                      {RESOURCE_TYPE_LABELS[space.type] || space.type}
                    </span>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-[#001c28]/5 flex items-center justify-center text-[#001c28]/30 group-hover:bg-[#d4a94d] group-hover:text-white transition-all shadow-sm">
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              ))
            )}
            {isAdmin && (
              <Link to="/spaces" className="w-full py-8 border-4 border-dashed border-[#001c28]/5 rounded-[2.5rem] flex flex-col items-center justify-center space-y-3 text-[#001c28]/30 hover:text-[#d4a94d] hover:border-[#d4a94d]/30 hover:bg-white transition-all font-black uppercase text-[10px] tracking-[0.3em] group shadow-sm bg-[#001c28]/[0.02]">
                <div className="w-10 h-10 rounded-full bg-white border-2 border-current flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-500" />
                </div>
                <span>Adicionar Novo Espaço</span>
              </Link>
            )}
          </div>

          {/* New Section: Recent Reviews */}

          {isAdmin && (
            <div className="space-y-6 pt-10">
              <div className="flex items-center justify-between border-b-2 border-[#001c28]/5 pb-6">
                <h2 className="text-xl font-black text-[#001c28] uppercase tracking-tight flex items-center">
                  <Star className="w-6 h-6 mr-4 text-[#d4a94d]" /> Avaliações Recentes
                </h2>
              </div>
              <div className="bg-white p-6 rounded-[2.5rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5">
                <div className="space-y-6">
                  {recentReviews.length === 0 ? (
                    <p className="text-center text-[#001c28]/40 text-xs font-bold uppercase tracking-widest py-4">Nenhuma avaliação recente</p>
                  ) : (
                    recentReviews.map((review) => (
                      <div key={review.id} className="flex gap-4 border-b border-[#001c28]/5 last:border-0 pb-4 last:pb-0">
                        <div className="w-10 h-10 bg-[#001c28]/5 rounded-full flex items-center justify-center text-[#001c28]/40 font-bold">
                          {review.userName?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <p className="text-xs font-bold text-[#001c28]">{review.userName}</p>
                            <div className="flex text-[#d4a94d]">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'fill-[#d4a94d]' : 'text-[#001c28]/10'}`} />
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-[#001c28]/60 mt-1 line-clamp-2">{review.comment}</p>
                          <p className="text-[9px] text-[#001c28]/30 font-bold uppercase tracking-widest mt-2">{review.createdAt?.toDate?.().toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}


          {/* Quick Actions Card */}
          <div className="bg-[#001c28] p-8 rounded-[2.5rem] text-white shadow-2xl shadow-[#001c28]/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4a94d] opacity-20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:bg-white group-hover:opacity-10 transition-colors duration-700"></div>
            <h3 className="text-lg font-black uppercase italic tracking-tighter mb-6 relative z-10">Ações Rápidas</h3>
            <div className="grid grid-cols-2 gap-4 relative z-10">
              <Link to="/reserve" className="bg-white/10 hover:bg-white hover:text-[#001c28] p-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all cursor-pointer backdrop-blur-sm">
                <CalendarPlus className="w-5 h-5" />
                <span className="text-[9px] font-black uppercase tracking-widest text-center">Nova Reserva</span>
              </Link>
              {isAdmin && (
                <Link to="/members" className="bg-white/10 hover:bg-white hover:text-[#001c28] p-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all cursor-pointer backdrop-blur-sm">
                  <Users className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-center">Gerenciar Membros</span>
                </Link>
              )}
              {isAdmin && (
                <Link to="/reports" className="col-span-2 bg-[#d4a94d] text-white hover:bg-white hover:text-[#d4a94d] p-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-[#d4a94d]/20">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Ver Relatórios</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
